import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import config from "../config";
import { jwtDecode } from "jwt-decode";

const baseUrl = config.baseURL;

const api = (token) =>
  axios.create({
    baseURL: baseUrl,
    headers: { Authorization: `Bearer ${token}` },
  });

const STATUS_COLORS = {
  pending: { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
  active: { bg: "#dcfce7", text: "#14532d", dot: "#22c55e" },
  rejected: { bg: "#f1f5f9", text: "#475569", dot: "#94a3b8" },
};

// ✅ FIX 1: keyed by plan_key string, not plan_id INT
const PLAN_COLORS = {
  free: "#16a34a",
  growth: "#2E4053",
  pro: "#7C3AED",
};

const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span
      style={{
        background: c.bg,
        color: c.text,
        fontSize: 12,
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: 20,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: c.dot,
          display: "inline-block",
        }}
      />
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

// ── Activate Modal ────────────────────────────────────────────────────────────
const ActivateModal = ({ request, onClose, onDone }) => {
  const [form, setForm] = useState({
    status: "active",
    expires_at: (() => {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().split("T")[0];
    })(),
    admin_note: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setLoading(true);
    setErr("");
    try {
      const token = localStorage.getItem("token");
      await api(token).post(`/api/subscription/activate/${request.id}`, form);
      onDone();
      onClose();
    } catch (e) {
      setErr(
        e?.response?.data?.message || "Failed to update. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <div
      style={AM.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={AM.modal}>
        <div style={AM.header}>
          <div>
            <div style={AM.subLabel}>Subscription Request #{request.id}</div>
            <div style={AM.name}>{request.full_name}</div>
          </div>
          <button onClick={onClose} style={AM.close}>
            ✕
          </button>
        </div>

        <div style={AM.infoGrid}>
          {[
            ["Plan", request.plan_name],
            [
              "Price",
              `EGP ${Number(request.plan_price || 0).toLocaleString()}`,
            ],
            ["Email", request.email],
            ["Phone", request.phone],
            ["Company", request.company_name || "—"],
            [
              "Submitted",
              new Date(request.created_at).toLocaleDateString("en-GB"),
            ],
          ].map(([k, v]) => (
            <div key={k} style={AM.infoItem}>
              <div style={AM.infoLabel}>{k}</div>
              <div style={AM.infoVal}>{v}</div>
            </div>
          ))}
        </div>

        {request.message && (
          <div style={AM.noteBox}>
            <div style={AM.noteLabel}>Customer Notes</div>
            <div style={AM.noteText}>{request.message}</div>
          </div>
        )}

        {request.admin_note && request.status !== "pending" && (
          <div
            style={{
              ...AM.noteBox,
              background: "#f0f9ff",
              borderColor: "#bae6fd",
              marginTop: 0,
            }}
          >
            <div style={{ ...AM.noteLabel, color: "#0369a1" }}>
              Previous Admin Note
            </div>
            <div style={AM.noteText}>{request.admin_note}</div>
          </div>
        )}

        <div style={AM.divider} />

        <div style={AM.fields}>
          <div style={AM.fieldRow}>
            <label style={AM.label}>Action</label>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((p) => ({ ...p, status: e.target.value }))
              }
              style={AM.select}
            >
              <option value="active">✅ Activate Plan</option>
              <option value="rejected">❌ Reject Request</option>
              <option value="pending">🕐 Keep as Pending</option>
            </select>
          </div>

          {form.status === "active" && (
            <div style={AM.fieldRow}>
              <label style={AM.label}>Expires On</label>
              <input
                type="date"
                value={form.expires_at}
                onChange={(e) =>
                  setForm((p) => ({ ...p, expires_at: e.target.value }))
                }
                style={AM.input}
              />
            </div>
          )}

          <div style={AM.fieldRow}>
            <label style={AM.label}>Admin Note (sent to user)</label>
            <textarea
              value={form.admin_note}
              onChange={(e) =>
                setForm((p) => ({ ...p, admin_note: e.target.value }))
              }
              placeholder={
                form.status === "active"
                  ? "Your plan is now active. Welcome to Spade ERP!"
                  : form.status === "rejected"
                  ? "We couldn't verify your payment. Please contact us."
                  : "We'll review your request shortly."
              }
              rows={3}
              style={{ ...AM.input, resize: "vertical" }}
            />
          </div>
        </div>

        {err && <div style={AM.errBox}>{err}</div>}

        <div style={AM.actions}>
          <button onClick={onClose} style={AM.cancelBtn}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading}
            style={{
              ...AM.submitBtn,
              background:
                form.status === "active"
                  ? "#16a34a"
                  : form.status === "rejected"
                  ? "#dc2626"
                  : "#2E4053",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading
              ? "Saving…"
              : form.status === "active"
              ? "✅ Activate & Notify User"
              : form.status === "rejected"
              ? "❌ Reject & Notify User"
              : "💾 Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Admin Panel ──────────────────────────────────────────────────────────
const SubscriptionAdmin = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  // ✅ FIX 2: replaced 'expired' with 'rejected' to match DB ENUM
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    active: 0,
    rejected: 0,
  });

  const user = (() => {
    try {
      const token = localStorage.getItem("token");
      return token ? jwtDecode(token) : null;
    } catch {
      return null;
    }
  })();

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await api(token).get("/api/subscription/requests");
      const list = res?.data?.data || res?.data || [];
      setRequests(list);
      // ✅ FIX 3: count 'rejected' not 'expired'
      setStats({
        total: list.length,
        pending: list.filter((r) => r.status === "pending").length,
        active: list.filter((r) => r.status === "active").length,
        rejected: list.filter((r) => r.status === "rejected").length,
      });
    } catch (e) {
      setErr("Failed to load subscription requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  if (!user || user.role !== "master") {
    return (
      <div style={{ textAlign: "center", marginTop: 80 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <h2 style={{ color: "#0f172a" }}>Access Restricted</h2>
        <p style={{ color: "#64748b" }}>
          This panel is only available to master administrators.
        </p>
      </div>
    );
  }

  const filtered = requests.filter((r) => {
    const matchFilter = filter === "all" || r.status === filter;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      r.full_name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.company_name?.toLowerCase().includes(q) ||
      r.plan_name?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  return (
    <div style={A.page}>
      <div style={A.topBar}>
        <div>
          <h1 style={A.title}>Subscription Requests</h1>
          <p style={A.subtitle}>Review, activate, and notify users</p>
        </div>
        <button onClick={fetchRequests} style={A.refreshBtn}>
          ↻ Refresh
        </button>
      </div>

      {/* ✅ FIX 2: stat cards use 'rejected' instead of 'expired' */}
      <div style={A.statsRow}>
        {[
          { label: "Total", val: stats.total, color: "#2E4053", bg: "#eef2ff" },
          {
            label: "Pending",
            val: stats.pending,
            color: "#d97706",
            bg: "#fef3c7",
          },
          {
            label: "Active",
            val: stats.active,
            color: "#16a34a",
            bg: "#dcfce7",
          },
          {
            label: "Rejected",
            val: stats.rejected,
            color: "#dc2626",
            bg: "#fee2e2",
          },
        ].map((s) => (
          <div key={s.label} style={{ ...A.statCard, background: s.bg }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>
              {s.val}
            </div>
            <div style={{ fontSize: 13, color: s.color, fontWeight: 500 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div style={A.toolBar}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, company, plan…"
          style={A.searchInput}
        />
        <div style={A.filterGroup}>
          {/* ✅ FIX 2: removed 'expired', kept only valid DB statuses */}
          {["all", "pending", "active", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                ...A.filterBtn,
                ...(filter === f ? A.filterActive : {}),
              }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === "pending" && stats.pending > 0 && (
                <span style={A.pendingDot}>{stats.pending}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {err && <div style={A.errBox}>{err}</div>}

      {loading ? (
        <div style={A.loadWrap}>
          <div style={A.spinner} />
          <p style={{ color: "#94a3b8", marginTop: 12 }}>Loading requests…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={A.emptyBox}>
          <div style={{ fontSize: 36 }}>📭</div>
          <p style={{ color: "#94a3b8", marginTop: 8 }}>No requests found.</p>
        </div>
      ) : (
        <div style={A.tableWrap}>
          <table style={A.table}>
            <thead>
              <tr>
                {[
                  "#",
                  "Name & Company",
                  "Plan",
                  "Price",
                  "Status",
                  "Expires",
                  "Submitted",
                  "Action",
                ].map((h) => (
                  <th key={h} style={A.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                // ✅ FIX 1: use plan_key for color lookup, not plan_id
                const planColor = PLAN_COLORS[r.plan_key] || "#2E4053";
                return (
                  <tr
                    key={r.id}
                    style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}
                  >
                    <td style={A.td}>{r.id}</td>
                    <td style={A.td}>
                      <div style={{ fontWeight: 600, color: "#0f172a" }}>
                        {r.full_name}
                      </div>
                      {r.company_name && (
                        <div style={{ fontSize: 12, color: "#94a3b8" }}>
                          {r.company_name}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {r.email}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {r.phone}
                      </div>
                    </td>
                    <td style={A.td}>
                      <span
                        style={{
                          background: planColor + "15",
                          color: planColor,
                          fontSize: 12,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 20,
                        }}
                      >
                        {r.plan_name}
                      </span>
                    </td>
                    <td style={{ ...A.td, fontWeight: 700, color: "#0f172a" }}>
                      EGP {Number(r.plan_price || 0).toLocaleString()}
                    </td>
                    <td style={A.td}>
                      <StatusBadge status={r.status} />
                    </td>
                    <td style={{ ...A.td, fontSize: 12, color: "#64748b" }}>
                      {r.expires_at
                        ? new Date(r.expires_at).toLocaleDateString("en-GB")
                        : "—"}
                    </td>
                    <td style={{ ...A.td, fontSize: 12, color: "#94a3b8" }}>
                      {new Date(r.created_at).toLocaleDateString("en-GB")}
                    </td>
                    <td style={A.td}>
                      <button
                        onClick={() => setSelected(r)}
                        style={{
                          ...A.actionBtn,
                          background:
                            r.status === "pending" ? "#2E4053" : "#f1f5f9",
                          color: r.status === "pending" ? "#fff" : "#2E4053",
                        }}
                      >
                        {r.status === "pending" ? "Review →" : "Edit"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <ActivateModal
          request={selected}
          onClose={() => setSelected(null)}
          onDone={fetchRequests}
        />
      )}
    </div>
  );
};

export default SubscriptionAdmin;

// ── Styles (unchanged) ────────────────────────────────────────────────────────
const A = {
  page: {
    padding: "28px 24px 60px",
    maxWidth: 1200,
    margin: "0 auto",
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  title: { fontSize: 26, fontWeight: 800, color: "#0f172a", margin: 0 },
  subtitle: { fontSize: 14, color: "#64748b", marginTop: 4 },
  refreshBtn: {
    padding: "9px 18px",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    color: "#374151",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))",
    gap: 14,
    marginBottom: 24,
  },
  statCard: { borderRadius: 12, padding: "16px 20px", textAlign: "center" },
  toolBar: {
    display: "flex",
    gap: 12,
    marginBottom: 20,
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchInput: {
    flex: 1,
    minWidth: 220,
    padding: "9px 14px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  },
  filterGroup: { display: "flex", gap: 6, flexWrap: "wrap" },
  filterBtn: {
    padding: "7px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: 20,
    background: "#fff",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  filterActive: {
    background: "#2E4053",
    color: "#fff",
    border: "1px solid #2E4053",
  },
  pendingDot: {
    background: "#f59e0b",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: "50%",
    width: 18,
    height: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errBox: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "12px 16px",
    color: "#dc2626",
    fontSize: 13,
    marginBottom: 16,
  },
  loadWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "60px 0",
  },
  spinner: {
    width: 32,
    height: 32,
    border: "3px solid #e5e7eb",
    borderTop: "3px solid #2E4053",
    borderRadius: "50%",
    animation: "spin .8s linear infinite",
  },
  emptyBox: { textAlign: "center", padding: "60px 0", color: "#94a3b8" },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,.06)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    minWidth: 800,
  },
  th: {
    padding: "12px 14px",
    background: "#f8fafc",
    fontWeight: 600,
    color: "#64748b",
    fontSize: 12,
    textAlign: "left",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
  },
  actionBtn: {
    padding: "7px 14px",
    border: "none",
    borderRadius: 7,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
};

const AM = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.55)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 520,
    maxHeight: "90vh",
    overflowY: "auto",
    padding: "28px 28px 24px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  subLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#94a3b8",
    letterSpacing: 1,
    marginBottom: 3,
  },
  name: { fontSize: 22, fontWeight: 800, color: "#0f172a" },
  close: {
    background: "none",
    border: "none",
    fontSize: 18,
    cursor: "pointer",
    color: "#94a3b8",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px 20px",
    background: "#f8fafc",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 14,
  },
  infoItem: { display: "flex", flexDirection: "column", gap: 2 },
  infoLabel: {
    fontSize: 11,
    color: "#94a3b8",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  infoVal: { fontSize: 13, color: "#0f172a", fontWeight: 500 },
  noteBox: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 14,
  },
  noteLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#92400e",
    marginBottom: 4,
  },
  noteText: { fontSize: 13, color: "#374151" },
  divider: { height: 1, background: "#f1f5f9", margin: "16px 0" },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    marginBottom: 16,
  },
  fieldRow: { display: "flex", flexDirection: "column", gap: 5 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  input: {
    padding: "10px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    padding: "10px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    background: "#fff",
    width: "100%",
  },
  errBox: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    color: "#dc2626",
    marginBottom: 12,
  },
  actions: { display: "flex", gap: 10, justifyContent: "flex-end" },
  cancelBtn: {
    padding: "11px 22px",
    background: "#f1f5f9",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    color: "#374151",
  },
  submitBtn: {
    padding: "11px 22px",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    color: "#fff",
  },
};
